import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { fmtET } from '../../utils/time';
import api, { asArray } from '../../services/api';
import RapidOptionsPanel from './RapidOptionsPanel';
import RapidMovesPanel from './RapidMovesPanel';

const empty = {
  symbol: '', companyName: '', signalType: 'STOCK_BUY',
  entryPrice: '', targetPrice: '', stopLoss: '',
  buyTarget: '', sellTarget1: '', gainOverridePct: '', 
  strikePrice: '', expiryDate: '', status: 'OPEN',
  // Was true — manually-created signals defaulted to published immediately, the one
  // creation path the "review before publish" policy hadn't reached. Now every
  // signal starts as a draft, generated or hand-entered, admin included — check the
  // Published box (or use the row's Publish button after) when it's ready.
  published: false, notificationEnabled: true,
  startDate: '', endDate: ''
};

const SCALP_LAST_SEEN_KEY = 'stockxignal_admin_scalp_last_seen';

/** Option candidate ([POC]) — a real-time stock move flagged as a CALL/PUT idea. */
function isPocTagged(s) {
  return (s.companyName || '').startsWith('[POC]');
}

function isScalpTagged(s) {
  const name = s.companyName || '';
  return name.startsWith('[SCALP]') || name.startsWith('[SWING]') || name.startsWith('[MTF]');
}

function calcGain(entry, live, signalType, isOption) {
  if (!entry || Number(entry) <= 0) return null;
  if (live == null || Number(live) <= 0) return null;
  const e = Number(entry), m = Number(live);
  if (isOption) return ((m - e) / e) * 100;
  if (signalType === 'STOCK_SELL') return ((e - m) / e) * 100;
  return ((m - e) / e) * 100;
}

export default function AdminSignals() {
  const [signals, setSignals] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // "Today's Buy/Short Formula" engine (see DailyFormulaSignalService) — replaced the
  // old backtest-driven single-tier generator behind the "AI Signal Today" button.
  // dailyRegime/dailyMode/dailyShown describe the most recent find scan; dailyEnabled
  // tracks whether the recurring 9:40/11:00/1:30/3:50 ET schedule is currently on.
  const [dailyRegime, setDailyRegime] = useState(null);
  const [dailyMode, setDailyMode] = useState(null);
  const [dailyShown, setDailyShown] = useState([]);
  const [dailyEnabled, setDailyEnabled] = useState(null); // null = not loaded yet
  const [dailyEnabledLoading, setDailyEnabledLoading] = useState(false);
  const [dailyPhaseLoading, setDailyPhaseLoading] = useState(null); // 'PROTECT' | 'PREPARE' | null
  const [dailyPhaseMsg, setDailyPhaseMsg] = useState('');
  const [filter, setFilter] = useState('PENDING');
  const [autoCloseMsg, setAutoCloseMsg] = useState('');
  const [autoCloseLoading, setAutoCloseLoading] = useState(false);
  const [scalpMsg, setScalpMsg] = useState('');
  const [scalpLoading, setScalpLoading] = useState(false);
  const [scalpScan, setScalpScan] = useState([]);
  const [scalpStyle, setScalpStyle] = useState('SCALP');
  const [newScalpCount, setNewScalpCount] = useState(0);

  // Intraday Option Formula engine (see IntradayOptionFormulaService) — the admin's
  // literal 2-in-1 CALL/PUT SCALP + SWING formula with its own auto-scan cadence
  // (2-min mornings, 5-min lunch, 1-min last hour), separate from the ScalpSignalService
  // panel above (rule-engine differs; that one stays available but isn't the primary
  // path now).
  const [iofEnabled, setIofEnabledState] = useState(null); // null = not loaded yet
  const [iofEnabledLoading, setIofEnabledLoading] = useState(false);
  const [iofLoading, setIofLoading] = useState(null); // 'PREPARE' | 'SCAN' | null
  const [iofPrepareResult, setIofPrepareResult] = useState(null);
  const [iofScanResult, setIofScanResult] = useState(null);

  const [scalpEnabled, setScalpEnabled] = useState(null); // null = not loaded yet
  const [scalpEnabledLoading, setScalpEnabledLoading] = useState(false);

  useEffect(() => {
    api.get('/signals/scalp/enabled').then((r) => setScalpEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const toggleScalpEnabled = async (checked) => {
    setScalpEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/scalp/enabled?enabled=${checked}`);
      setScalpEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setScalpEnabledLoading(false);
    }
  };

  const [swingEnabled, setSwingEnabled] = useState(null); // null = not loaded yet
  const [swingEnabledLoading, setSwingEnabledLoading] = useState(false);

  useEffect(() => {
    api.get('/signals/swing/enabled').then((r) => setSwingEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const toggleSwingEnabled = async (checked) => {
    setSwingEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/swing/enabled?enabled=${checked}`);
      setSwingEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSwingEnabledLoading(false);
    }
  };

  const [rapidEnabled, setRapidEnabled] = useState(null); // null = not loaded yet
  const [rapidEnabledLoading, setRapidEnabledLoading] = useState(false);

  useEffect(() => {
    api.get('/signals/rapid-move/enabled').then((r) => setRapidEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const toggleRapidEnabled = async (checked) => {
    setRapidEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/rapid-move/enabled?enabled=${checked}`);
      setRapidEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setRapidEnabledLoading(false);
    }
  };

  const loadIofEnabled = async () => {
    try {
      const r = await api.get('/signals/iof/enabled');
      setIofEnabledState(!!r.data?.enabled);
    } catch (_) {}
  };
  useEffect(() => { loadIofEnabled(); }, []);

  const toggleIofEnabled = async (checked) => {
    setIofEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/iof/enabled?enabled=${checked}`);
      setIofEnabledState(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setIofEnabledLoading(false);
    }
  };

  const runIofPrepare = async () => {
    setIofLoading('PREPARE');
    try {
      const r = await api.post('/signals/iof/prepare');
      setIofPrepareResult(r.data);
    } catch (err) {
      setIofPrepareResult({ message: err.response?.data?.message || 'Prepare failed' });
    } finally {
      setIofLoading(null);
    }
  };

  const runIofScan = async () => {
    setIofLoading('SCAN');
    try {
      const r = await api.post('/signals/iof/scan');
      setIofScanResult(r.data);
      await load();
    } catch (err) {
      setIofScanResult({ message: err.response?.data?.message || 'Scan failed' });
    } finally {
      setIofLoading(null);
    }
  };

  // Portfolio-level protection (see PortfolioProtectionService) — a cross-engine
  // daily loss limit + combined open-position cap, layered on top of every
  // engine's own per-signal stop/target. Status polled alongside the 20s signal
  // refresh so the "paused" banner stays live.
  const [portfolioStatus, setPortfolioStatus] = useState(null);
  const [portfolioSettingsLoading, setPortfolioSettingsLoading] = useState(false);
  const [portfolioForm, setPortfolioForm] = useState({ accountValue: '', dailyLossLimitPct: '', maxConcurrentOpen: '' });

  const loadPortfolioStatus = async () => {
    try {
      const r = await api.get('/signals/portfolio/status');
      setPortfolioStatus(r.data || null);
      setPortfolioForm({
        accountValue: r.data?.accountValue ?? '',
        dailyLossLimitPct: r.data?.dailyLossLimitPct ?? '',
        maxConcurrentOpen: r.data?.maxConcurrentOpen ?? '',
      });
    } catch (_) {}
  };
  useEffect(() => {
    loadPortfolioStatus();
    const id = setInterval(loadPortfolioStatus, 60_000);
    return () => clearInterval(id);
  }, []);

  const savePortfolioSettings = async () => {
    setPortfolioSettingsLoading(true);
    try {
      const params = new URLSearchParams();
      if (portfolioForm.accountValue !== '') params.set('accountValue', portfolioForm.accountValue);
      if (portfolioForm.dailyLossLimitPct !== '') params.set('dailyLossLimitPct', portfolioForm.dailyLossLimitPct);
      if (portfolioForm.maxConcurrentOpen !== '') params.set('maxConcurrentOpen', portfolioForm.maxConcurrentOpen);
      const r = await api.patch(`/signals/portfolio/settings?${params.toString()}`);
      setPortfolioStatus(r.data || null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update portfolio settings');
    } finally {
      setPortfolioSettingsLoading(false);
    }
  };

  const resumePortfolioToday = async () => {
    try {
      await api.post('/signals/portfolio/resume-today');
      await loadPortfolioStatus();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to resume');
    }
  };

  // Top-level section switcher — was one long scrolling page with every panel always
  // rendered; now only the active section's panel is shown, per admin request.
  const SECTIONS = [
    { id: 'AI', label: 'AI Signal (Today)' },
    { id: 'HORIZON', label: 'Multi-Horizon AI' },
    { id: 'SCALP', label: 'Intraday Scalp / Swing' },
    { id: 'ADXIDM', label: 'ADX/IDM' },
  ];
  const [activeSection, setActiveSection] = useState('AI');

  // Shared TIGHT/MEDIUM/LOOSE scan-gate dial — one admin-wide setting all 4 engines
  // (AI Signal/DAILY, Multi-Horizon AI/MHF, Intraday Option Formula, ADX/IDM) read on
  // their next scan tick. MEDIUM = each engine's own original defaults, unchanged.
  const [gateMode, setGateModeState] = useState('MEDIUM');
  const [gateModeLoading, setGateModeLoading] = useState(false);
  const [gateModeMsg, setGateModeMsg] = useState('');

  const loadGateMode = async () => {
    try {
      const r = await api.get('/signals/gate-mode');
      setGateModeState(r.data?.mode || 'MEDIUM');
    } catch (_) {}
  };

  const setGateMode = async (mode) => {
    setGateModeMsg('');
    setGateModeLoading(true);
    try {
      const r = await api.patch(`/signals/gate-mode?mode=${encodeURIComponent(mode)}`);
      setGateModeState(r.data?.mode || mode);
      setGateModeMsg(r.data?.message || '');
    } catch (err) {
      setGateModeMsg(err.response?.data?.message || 'Failed to update gate mode');
    } finally {
      setGateModeLoading(false);
    }
  };

  useEffect(() => { loadGateMode(); }, []);

  // Single-symbol MTF (multi-timeframe + ADX/DMI) scalp engine — its own top-level
  // "ADX/IDM" tab (moved out of Intraday Scalp/Swing per admin request, since it's a
  // distinct engine with its own tracked-symbol + manual-scan workflow, not just
  // another options pick generator).
  //
  // "Tracked symbol" = the ONE symbol MtfScalpAutoGenerateScheduler auto-scans every
  // 5 minutes (targeting 10 alerts/day). Defaults to QQQ on the backend. Setting a
  // new one replaces the old outright — only one tracked symbol at a time, matching
  // "cancel and put a new symbol" — and "Cancel tracking" stops auto-generation
  // entirely until a new symbol is set.
  const [mtfTrackedSymbol, setMtfTrackedSymbol] = useState(''); // currently tracked, per backend
  const [mtfTrackedLoading, setMtfTrackedLoading] = useState(false);
  const [mtfTrackedMsg, setMtfTrackedMsg] = useState('');
  const [mtfNewSymbol, setMtfNewSymbol] = useState('QQQ'); // input for setting a new tracked symbol

  const loadMtfTrackedSymbol = async () => {
    try {
      const r = await api.get('/signals/mtf-scalp/tracked-symbol');
      setMtfTrackedSymbol(r.data?.symbol || '');
    } catch (_) {}
  };

  const setTrackedSymbol = async (symbolOrNull) => {
    setMtfTrackedMsg('');
    setMtfTrackedLoading(true);
    try {
      const params = symbolOrNull ? `?symbol=${encodeURIComponent(symbolOrNull)}` : '';
      const r = await api.patch(`/signals/mtf-scalp/tracked-symbol${params}`);
      setMtfTrackedSymbol(r.data?.symbol || '');
      setMtfTrackedMsg(r.data?.message || '');
    } catch (err) {
      setMtfTrackedMsg(err.response?.data?.message || 'Failed to update tracked symbol');
    } finally {
      setMtfTrackedLoading(false);
    }
  };

  useEffect(() => { loadMtfTrackedSymbol(); }, []);

  // MTF auto-scan on/off (backend default OFF — it was the biggest loss source).
  const [mtfEnabled, setMtfEnabled] = useState(null); // null = not loaded yet
  const [mtfEnabledLoading, setMtfEnabledLoading] = useState(false);
  useEffect(() => {
    api.get('/signals/mtf/enabled').then((r) => setMtfEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);
  const toggleMtfEnabled = async (checked) => {
    setMtfEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/mtf/enabled?enabled=${checked}`);
      setMtfEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setMtfEnabledLoading(false);
    }
  };

  // Manual one-off scan (any symbol, doesn't change what's auto-tracked above).
  // Always unpublished server-side — no publish param sent or accepted.
  const [mtfSymbol, setMtfSymbol] = useState('QQQ');
  const [mtfLoading, setMtfLoading] = useState(false);
  const [mtfMsg, setMtfMsg] = useState('');
  const [mtfRead, setMtfRead] = useState(null);

  const generateMtf = async () => {
    const sym = mtfSymbol.trim().toUpperCase();
    if (!sym) return;
    setMtfMsg('');
    setMtfRead(null);
    setMtfLoading(true);
    try {
      const r = await api.post(`/signals/mtf-scalp?symbol=${encodeURIComponent(sym)}`);
      const d = r.data || {};
      setMtfMsg(d.message || '');
      setMtfRead(d.read || null);
      await load();
    } catch (err) {
      setMtfMsg(err.response?.data?.message || 'Scan failed');
    } finally {
      setMtfLoading(false);
    }
  };

  const recomputeNewScalpCount = (list) => {
    const lastSeen = Number(localStorage.getItem(SCALP_LAST_SEEN_KEY) || 0);
    const count = (list || []).filter(s => {
      if (!isScalpTagged(s)) return false;
      const t = s.createdAt ? new Date(s.createdAt).getTime() : 0;
      return t > lastSeen;
    }).length;
    setNewScalpCount(count);
  };

  const markScalpSeen = () => {
    localStorage.setItem(SCALP_LAST_SEEN_KEY, String(Date.now()));
    setNewScalpCount(0);
  };

  const load = async () => {
    const [r, weeklyR] = await Promise.all([
      api.get('/signals/all'),
      api.get('/weekly-signals/all').catch(() => ({ data: [] })),
    ]);
    const plain = asArray(r.data);
    // AI Rapid Profit (WeeklyAiSignal) merged into this same review inbox — same
    // published/pending/publish/discard/close review flow as every other engine, no
    // separate review page anymore. It's a distinct backend model (no buyTarget/
    // sellTarget1 — options use entryPrice/targetPrice for both), so normalize those
    // two fields and tag `source: 'WEEKLY'` so publish/discard/close/remove below know
    // to call the /weekly-signals/* endpoints instead of /signals/*.
    const weekly = asArray(weeklyR.data).map(w => ({
      ...w,
      source: 'WEEKLY',
      sellTarget1: w.sellTarget1 ?? w.targetPrice,
    }));
    const list = [...plain, ...weekly];
    setSignals(list);
    recomputeNewScalpCount(list);
    const symbols = [...new Set(list.map(s => s.symbol).filter(Boolean))];
    const map = {};
    for (const sym of symbols.slice(0, 5)) {
      try {
        const res = await api.get(`/stock/price/${sym}`);
        if (res.data?.success) map[sym] = res.data;
      } catch (_) {}
    }
    setQuotes(map);
  };

  // Poll every 20s so a flashing badge can surface a new intraday (Scalp/Swing) pick
  // — e.g. one generated by another admin — without needing a manual refresh.
  useEffect(() => {
    load();
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      entryPrice: form.entryPrice ? parseFloat(form.entryPrice) : null,
      targetPrice: form.targetPrice ? parseFloat(form.targetPrice) : null,
      stopLoss: form.stopLoss ? parseFloat(form.stopLoss) : null,
      buyTarget: form.buyTarget ? parseFloat(form.buyTarget) : null,
      sellTarget1: form.sellTarget1 ? parseFloat(form.sellTarget1) : null,
      gainOverridePct: form.gainOverridePct !== '' && form.gainOverridePct != null ? parseFloat(form.gainOverridePct) : null,
      
      strikePrice: form.strikePrice ? parseFloat(form.strikePrice) : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };
    try {
      if (editingId) await api.put(`/signals/${editingId}`, payload);
      else await api.post('/signals', payload);
    } catch (e) {
      // e.g. publishing an option candidate without its premium
      alert(e.response?.data?.message || 'Save failed');
      return;
    }
    setForm(empty); setEditingId(null); setShowForm(false);
    load();
  };

  const edit = (s) => {
    setForm({
      symbol: s.symbol || '', companyName: s.companyName || '', signalType: s.signalType || 'STOCK_BUY',
      entryPrice: s.entryPrice ?? '', targetPrice: s.targetPrice ?? '', stopLoss: s.stopLoss ?? '',
      buyTarget: s.buyTarget ?? '', sellTarget1: s.sellTarget1 ?? '', gainOverridePct: s.gainOverridePct ?? '', 
      strikePrice: s.strikePrice ?? '', expiryDate: s.expiryDate || '', status: s.status || 'OPEN',
      published: s.published, notificationEnabled: s.notificationEnabled,
      startDate: s.startDate ? s.startDate.substring(0, 16) : '',
      endDate: s.endDate ? s.endDate.substring(0, 16) : ''
    });
    setEditingId(s.id); setShowForm(true);
  };

  const remove = async (s) => {
    if (!confirm('Delete?')) return;
    // AI Rapid Profit (WeeklyAiSignal) has no delete endpoint — Delete is hidden for
    // it below (see the row actions); only plain StockSignal rows call this.
    await api.delete(`/signals/${s.id}`);
    load();
  };

  const closeSignal = async (s) => {
    if (!confirm('Close this signal now? (marks Booked Profit if still OPEN)')) return;
    try {
      await api.patch(s.source === 'WEEKLY' ? `/weekly-signals/${s.id}/close` : `/signals/${s.id}/close`);
      await load();
    } catch (e) {
      alert(e.response?.data?.message || 'Close failed');
    }
  };

  // Quick actions for a reviewed pending signal — publish as-is (no value changes
  // needed) or discard it outright. To change a value first, use Edit instead: its
  // form already includes a "Published" checkbox, so reviewing/editing and
  // publishing can happen in the same save. AI Rapid Profit (WeeklyAiSignal) picks
  // route to their own /weekly-signals/* endpoints — same publish/discard actions,
  // different backend model, now reviewed from this one unified list.
  const publishPending = async (s) => {
    // Option candidates have no premium until the admin enters one — open Edit instead.
    if (isPocTagged(s) && (s.buyTarget == null || Number(s.buyTarget) <= 0)) {
      alert('Enter the option premium (Buy Target) first — opening Edit.');
      edit(s);
      return;
    }
    try {
      await api.patch(s.source === 'WEEKLY' ? `/weekly-signals/${s.id}/publish` : `/signals/${s.id}/publish`);
      await load();
    } catch (e) {
      alert(e.response?.data?.message || 'Publish failed');
    }
  };

  const discardPending = async (s) => {
    if (!confirm('Discard this pending signal? It will never be published.')) return;
    try {
      if (s.source === 'WEEKLY') {
        await api.patch(`/weekly-signals/${s.id}/reject`);
      } else {
        await api.patch(`/signals/${s.id}/discard`);
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.message || 'Discard failed');
    }
  };

  // Clicking "AI Signal Today" runs an immediate find-scan of today's Buy/Short
  // Formula AND turns on the recurring 4x/day schedule server-side (see
  // SignalController#aiDaily) — reload the enabled toggle too so the UI reflects that.
  const generateAiDaily = async () => {
    setAiMsg('');
    setAiLoading(true);
    try {
      const r = await api.post('/signals/ai-daily');
      const d = r.data || {};
      const created = d.created || [];
      setAiMsg(d.message || (created.length ? `Alerted ${created.length} signal(s)` : 'No signals created'));
      setDailyRegime(d.regime || null);
      setDailyMode(d.mode || null);
      setDailyShown(d.shown || []);
      setDailyEnabled(true);
      await load();
    } catch (err) {
      setAiMsg(err.response?.data?.message || 'Scan failed');
    } finally {
      setAiLoading(false);
    }
  };

  const loadDailyEnabled = async () => {
    try {
      const r = await api.get('/signals/daily-formula/enabled');
      setDailyEnabled(!!r.data?.enabled);
    } catch (_) {}
  };
  useEffect(() => { loadDailyEnabled(); }, []);

  const toggleDailyEnabled = async (checked) => {
    setDailyEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/daily-formula/enabled?enabled=${checked}`);
      setDailyEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setDailyEnabledLoading(false);
    }
  };

  // Manual trigger for the 11:00am/1:30pm "protect" check or the 3:50pm
  // "prepare for tomorrow" close-out, outside their fixed ET times (useful for
  // testing, or catching up if the server restarted mid-day).
  const runDailyPhase = async (phase) => {
    setDailyPhaseLoading(phase);
    setDailyPhaseMsg('');
    try {
      const r = await api.post(`/signals/daily-formula/run?phase=${phase}`);
      setDailyPhaseMsg(r.data?.message || `${phase} done`);
      await load();
    } catch (err) {
      setDailyPhaseMsg(err.response?.data?.message || `${phase} failed`);
    } finally {
      setDailyPhaseLoading(null);
    }
  };

  // Multi-Horizon Formula engine (see MultiHorizonFormulaService) — replaces the old
  // backtest-driven Multi-Horizon AI generator. 4 literal formulas, each with its own
  // scan cadence (see MultiHorizonFormulaAutoScheduler) and its own 4 adjustable
  // switches (Market/Trend/Strength/Location).
  const TIERS = [
    { id: 'ONE_TO_THREE_DAY', tag: '1-3D', label: '1-3 Day (fast)', cadence: 'Auto: every 30 min, 9:40am-3:40pm ET' },
    { id: 'ONE_WEEK', tag: '1W', label: '1 Week (best)', cadence: 'Auto: 9:40am ET scan, 3:50pm check, 4:00pm watchlist' },
    { id: 'FIFTEEN_DAY', tag: '15D', label: '15 Day', cadence: 'Auto: 10:00am ET, once/day' },
    { id: 'ONE_MONTH', tag: '1M', label: '1 Month (highest win)', cadence: 'Auto: 10:00am ET, Mon/Wed/Fri only' },
  ];
  const [mhfEnabled, setMhfEnabledState] = useState(null); // null = not loaded yet
  const [mhfEnabledLoading, setMhfEnabledLoading] = useState(false);
  const [mhfSwitches, setMhfSwitchesState] = useState(null); // { ONE_TO_THREE_DAY: {...}, ... }
  const [mhfLoading, setMhfLoading] = useState(null); // tier id currently running, or `${tier}-watchlist`
  const [mhfResults, setMhfResults] = useState({}); // { [tierId]: resultObj }

  const loadMhfEnabled = async () => {
    try {
      const r = await api.get('/signals/mhf/enabled');
      setMhfEnabledState(!!r.data?.enabled);
    } catch (_) {}
  };
  const loadMhfSwitches = async () => {
    try {
      const r = await api.get('/signals/mhf/switches');
      setMhfSwitchesState(r.data || null);
    } catch (_) {}
  };
  useEffect(() => { loadMhfEnabled(); loadMhfSwitches(); }, []);

  const toggleMhfEnabled = async (checked) => {
    setMhfEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/mhf/enabled?enabled=${checked}`);
      setMhfEnabledState(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setMhfEnabledLoading(false);
    }
  };

  // One of the 4 core switches for one tier. omit the other 3 params — PATCH only
  // sends the one that changed.
  const setMhfSwitch = async (tierId, patch) => {
    try {
      const params = new URLSearchParams(patch).toString();
      const r = await api.patch(`/signals/mhf/switches/${tierId}?${params}`);
      setMhfSwitchesState(prev => ({ ...(prev || {}), [tierId]: r.data }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update switch');
    }
  };

  const runMhfTier = async (tierId, scanForNew = true) => {
    setMhfLoading(tierId);
    try {
      const r = await api.post(`/signals/mhf/run/${tierId}?scanForNew=${scanForNew}`);
      setMhfResults(prev => ({ ...prev, [tierId]: r.data }));
      await load();
    } catch (err) {
      setMhfResults(prev => ({ ...prev, [tierId]: { message: err.response?.data?.message || 'Scan failed' } }));
    } finally {
      setMhfLoading(null);
    }
  };

  const runMhfWatchlist = async (tierId) => {
    setMhfLoading(`${tierId}-watchlist`);
    try {
      const r = await api.get(`/signals/mhf/watchlist/${tierId}`);
      setMhfResults(prev => ({ ...prev, [`${tierId}-watchlist`]: r.data }));
    } catch (err) {
      setMhfResults(prev => ({ ...prev, [`${tierId}-watchlist`]: { message: err.response?.data?.message || 'Watchlist failed' } }));
    } finally {
      setMhfLoading(null);
    }
  };

  const runAutoClose = async () => {
    setAutoCloseMsg('');
    setAutoCloseLoading(true);
    try {
      const r = await api.post('/signals/run-auto-close');
      const n = r.data?.closed ?? 0;
      setAutoCloseMsg(r.data?.message || `Closed ${n} signal(s)`);
      await load();
    } catch (err) {
      setAutoCloseMsg(err.response?.data?.message || 'Auto-close failed');
    } finally {
      setAutoCloseLoading(false);
    }
  };

  const generateScalp = async () => {
    setScalpMsg('');
    setScalpScan([]);
    setScalpLoading(true);
    try {
      const r = await api.post(`/signals/scalp?style=${scalpStyle}`);
      const d = r.data || {};
      const created = d.created || [];
      setScalpMsg(d.message || (created.length ? `Created ${created.length} ${scalpStyle.toLowerCase()} pick(s)` : 'No picks created'));
      setScalpScan(d.scan || []);
      await load();
      markScalpSeen();
    } catch (err) {
      setScalpMsg(err.response?.data?.message || 'Generate failed');
    } finally {
      setScalpLoading(false);
    }
  };

  const isClosed = (s) => {
    const st = (s.status || '').toUpperCase().replace(/\s+/g, '_');
    if (s.endDate) return true;
    return ['BOOKED_PROFIT', 'STOPPED_OUT', 'CLOSED'].includes(st);
  };

  // Live cross-engine pending-review count for the bell icon — every generation
  // path (Daily/Multi-Horizon/Intraday Option/Scalp/Swing/MTF/classic AI) always
  // creates unpublished, so this is the FULL review queue regardless of engine,
  // kept fresh by the existing 20s poll of load() above.
  const pendingCount = signals.filter(s => !isClosed(s) && !s.published).length;

  // Eye-icon inline preview — expands a row's full detail (all target levels,
  // strike/expiry, risk:reward) without switching into the Edit form.
  const [previewId, setPreviewId] = useState(null);

  const filteredSignals = signals.filter(s => {
    const opt = !!s.signalType?.startsWith('OPTION');
    const closed = isClosed(s);
    const isScalp = !!(s.companyName || '').startsWith('[SCALP]') || !!(s.companyName || '').startsWith('[SWING]') || !!(s.companyName || '').startsWith('[MTF]');
    // Was scoped to scalp/swing/MTF only — Multi-Horizon AI and the classic AI-daily
    // generator now ALSO always generate unpublished (see SignalController), so this
    // is every unpublished, not-yet-closed signal regardless of engine — that's the
    // full review queue now: edit, discard (delete), or publish each one before a
    // user ever sees it.
    if (filter === 'PENDING') return !closed && !s.published;
    if (filter === 'SCALP') return isScalp; // all scalp + swing activity, open + closed, in one place
    if (filter === 'OPEN') return !closed && !isScalp;
    if (filter === 'STOCK') return !opt && !closed && !isScalp;
    if (filter === 'OPTION') return opt && !closed && !isScalp;
    if (filter === 'CLOSED') return closed;
    return true; // ALL
  });
  const isOption = form.signalType?.startsWith('OPTION');

  const lookupSymbol = async (sym) => {
    if (!sym || sym.length < 1) return;
    try {
      const res = await api.get(`/stock/lookup?q=${encodeURIComponent(sym)}`);
      if (res.data?.bestName) {
        setForm(f => ({ ...f, companyName: res.data.bestName, symbol: (res.data.bestSymbol || sym).toUpperCase() }));
      }
    } catch (_) {}
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Manage Signals</h1>
          <button
            type="button"
            onClick={() => setFilter('PENDING')}
            className="relative flex items-center gap-1.5 text-sm bg-white border border-slate-200 hover:border-slate-300 rounded-full px-3 py-1.5"
            title={`${pendingCount} signal(s) awaiting review across every engine`}
          >
            <span aria-hidden="true">🔔</span>
            <span className="font-semibold text-slate-700">{pendingCount}</span>
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-600" />
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runAutoClose}
            disabled={autoCloseLoading}
            className="text-sm bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
            title="Closes any open signal whose target/stop has been hit, or whose holding window has expired (also runs automatically at 4:05pm ET)"
          >
            {autoCloseLoading ? 'Running…' : 'Run Auto-Close'}
          </button>
          <button onClick={() => {
              if (!showForm) {
                const now = new Date();
                const pad = (n) => String(n).padStart(2, '0');
                const local = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                setForm({ ...empty, startDate: local });
              } else {
                setForm(empty);
              }
              setEditingId(null);
              setShowForm(!showForm);
            }}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm">
            {showForm ? 'Cancel' : '+ New Signal'}
          </button>
        </div>
      </div>

      {autoCloseMsg && (
        <p className="text-xs text-slate-600 -mt-4 mb-4">{autoCloseMsg}</p>
      )}

      {portfolioStatus && (
        <div className={`rounded-xl border p-3 mb-4 text-xs flex flex-wrap items-center justify-between gap-2 ${
          portfolioStatus.pausedToday ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-600'
        }`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold">
              {portfolioStatus.pausedToday ? '⛔ New-entry scanning PAUSED for today' : '✅ Portfolio protection active'}
            </span>
            <span>Realized today: {portfolioStatus.realizedPnlDollars != null ? `$${portfolioStatus.realizedPnlDollars}` : '—'}
              {portfolioStatus.realizedLossPct != null && portfolioStatus.realizedLossPct > 0 ? ` (-${portfolioStatus.realizedLossPct}%)` : ''}
              {' '}/ limit {portfolioStatus.dailyLossLimitPct}%
            </span>
            <span>Open positions: {portfolioStatus.combinedOpenPositions} / {portfolioStatus.maxConcurrentOpen}
              {portfolioStatus.combinedCapReached ? ' (cap reached)' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {portfolioStatus.pausedToday && (
              <button type="button" onClick={resumePortfolioToday} className="text-xs bg-rose-600 hover:bg-rose-700 text-white font-semibold px-3 py-1 rounded-lg">
                Resume today
              </button>
            )}
            <details className="relative">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none">Settings</summary>
              <div className="absolute right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-64 z-10 space-y-2">
                <label className="block text-[11px] text-slate-500">
                  Account value ($)
                  <input type="number" step="1" className="border rounded px-2 py-1 w-full text-xs mt-0.5"
                    value={portfolioForm.accountValue}
                    onChange={e => setPortfolioForm({ ...portfolioForm, accountValue: e.target.value })} />
                </label>
                <label className="block text-[11px] text-slate-500">
                  Daily loss limit (%)
                  <input type="number" step="0.5" className="border rounded px-2 py-1 w-full text-xs mt-0.5"
                    value={portfolioForm.dailyLossLimitPct}
                    onChange={e => setPortfolioForm({ ...portfolioForm, dailyLossLimitPct: e.target.value })} />
                </label>
                <label className="block text-[11px] text-slate-500">
                  Max combined open positions
                  <input type="number" step="1" className="border rounded px-2 py-1 w-full text-xs mt-0.5"
                    value={portfolioForm.maxConcurrentOpen}
                    onChange={e => setPortfolioForm({ ...portfolioForm, maxConcurrentOpen: e.target.value })} />
                </label>
                <button type="button" onClick={savePortfolioSettings} disabled={portfolioSettingsLoading}
                  className="w-full text-xs bg-slate-900 hover:bg-slate-800 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
                  {portfolioSettingsLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </details>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="text-xs">
          <span className="font-semibold text-slate-800">Scan gate:</span>{' '}
          <span className="text-slate-400">applies to all 4 engines below (AI Signal, Multi-Horizon AI, Intraday Option Formula, ADX/IDM)</span>
        </div>
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1">
          {['TIGHT', 'MEDIUM', 'LOOSE'].map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setGateMode(m)}
              disabled={gateModeLoading}
              className={`text-xs px-3 py-1.5 rounded-lg transition disabled:opacity-50 ${
                gateMode === m ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {m === 'TIGHT' ? 'Tight (fewer, higher-conviction)' : m === 'LOOSE' ? 'Loose (more alerts)' : 'Medium (default)'}
            </button>
          ))}
        </div>
        {gateModeMsg && <p className="text-[11px] text-slate-500">{gateModeMsg}</p>}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map(sec => (
          <button
            key={sec.id}
            type="button"
            onClick={() => setActiveSection(sec.id)}
            className={`text-sm font-semibold px-4 py-2.5 rounded-xl border transition ${
              activeSection === sec.id
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900'
            }`}
          >
            {sec.label}
          </button>
        ))}
      </div>

      {activeSection === 'AI' && (
      <>
      <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="text-sm font-semibold text-slate-800">Today's Buy/Short Formula</h2>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!dailyEnabled}
              disabled={dailyEnabled === null || dailyEnabledLoading}
              onChange={e => toggleDailyEnabled(e.target.checked)}
            />
            Auto-scan 4x/day
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Scans S&amp;P 500 + Nasdaq 100 (starter list — see backend/universe files) at four fixed ET times:
          <strong> 9:40am</strong> find new candidates, <strong>11:00am</strong> &amp; <strong>1:30pm</strong> protect
          open positions (check target/stop, move stop to breakeven once target is touched), and
          <strong> 3:50pm</strong> prepare for tomorrow (close out anything still open at the market). Regime gate:
          BUY day only if SPY is above its own 20-day average; SHORT day (SPY red) only if it's below. Up to 10
          scanned candidates are shown below either way; only the top 5 that clear all 4 criteria are alerted as
          pending signals for review below. Always unpublished — review, discard, or publish each one from the list
          further down this page.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={generateAiDaily}
            disabled={aiLoading}
            className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
          >
            {aiLoading ? 'Scanning…' : 'AI Signal Today (find now)'}
          </button>
          <button
            type="button"
            onClick={() => runDailyPhase('PROTECT')}
            disabled={dailyPhaseLoading != null}
            className="text-xs bg-sky-600 hover:bg-sky-700 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
            title="Manually run the 11:00am/1:30pm protect check now"
          >
            {dailyPhaseLoading === 'PROTECT' ? 'Protecting…' : 'Protect now'}
          </button>
          <button
            type="button"
            onClick={() => runDailyPhase('PREPARE')}
            disabled={dailyPhaseLoading != null}
            className="text-xs bg-slate-700 hover:bg-slate-800 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
            title="Manually run the 3:50pm prepare-for-tomorrow close-out now"
          >
            {dailyPhaseLoading === 'PREPARE' ? 'Closing out…' : 'Prepare for tomorrow now'}
          </button>
        </div>
        {aiMsg && <p className="text-xs text-slate-600 mt-2">{aiMsg}</p>}
        {dailyPhaseMsg && <p className="text-xs text-slate-600 mt-1">{dailyPhaseMsg}</p>}
      </div>
      </>
      )}


      {activeSection === 'HORIZON' && (
      <>
      <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 className="text-sm font-semibold text-slate-800">Multi-Horizon AI — 4 Formulas (1-3 Day / 1 Week / 15 Day / 1 Month)</h2>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!mhfEnabled}
              disabled={mhfEnabled === null || mhfEnabledLoading}
              onChange={e => toggleMhfEnabled(e.target.checked)}
            />
            Auto-scan new entries
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Each tier's own $500/2%-risk, 60-day-backtested formula (S&amp;P 500 + Nasdaq 100 only), long/buy-only.
          Turning "Auto-scan" off only pauses NEW entries — every tier's already-open positions keep being
          managed (stop/target/early-exit) on that tier's own cadence regardless, plus the SPY circuit breaker
          (drop &gt;1% in a trailing hour closes everything, across all 4 tiers) checked every 5 minutes on its
          own. Always generated unpublished — review each candidate below before it goes live.
        </p>

        <div className="space-y-3">
          {TIERS.map(t => {
            const sw = mhfSwitches?.[t.id];
            const res = mhfResults[t.id];
            const wl = mhfResults[`${t.id}-watchlist`];
            return (
              <div key={t.id} className="bg-slate-50 rounded-xl p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{t.label}</p>
                    <p className="text-[10px] text-slate-400">{t.cadence}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => runMhfTier(t.id, true)}
                      disabled={mhfLoading != null}
                      className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      {mhfLoading === t.id ? 'Scanning…' : 'Scan now'}
                    </button>
                    <button
                      type="button"
                      onClick={() => runMhfTier(t.id, false)}
                      disabled={mhfLoading != null}
                      className="text-xs bg-slate-600 hover:bg-slate-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                      title="Manage this tier's already-open positions only — no new entries"
                    >
                      Manage only
                    </button>
                    {t.id === 'ONE_WEEK' && (
                      <button
                        type="button"
                        onClick={() => runMhfWatchlist(t.id)}
                        disabled={mhfLoading != null}
                        className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                        title="Informational tomorrow-watchlist pass — creates no signals"
                      >
                        {mhfLoading === `${t.id}-watchlist` ? 'Checking…' : 'Watchlist'}
                      </button>
                    )}
                  </div>
                </div>

                {/* The 4 core switches */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-white rounded-lg px-2 py-1.5 border border-slate-200">
                    <input type="checkbox" checked={!!sw?.marketOn} disabled={!sw}
                      onChange={e => setMhfSwitch(t.id, { marketOn: e.target.checked })} />
                    Market (SPY&gt;20d)
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-white rounded-lg px-2 py-1.5 border border-slate-200">
                    <input type="checkbox" checked={!!sw?.trendOn} disabled={!sw}
                      onChange={e => setMhfSwitch(t.id, { trendOn: e.target.checked })} />
                    Trend (Price&gt;20&gt;50)
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-white rounded-lg px-2 py-1.5 border border-slate-200">
                    Strength
                    <select
                      className="text-[11px] border border-slate-200 rounded px-1 py-0.5 ml-auto"
                      value={sw?.strength || 'TIGHT'} disabled={!sw}
                      onChange={e => setMhfSwitch(t.id, { strength: e.target.value })}
                    >
                      <option value="TIGHT">TIGHT</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="OPEN">OPEN</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-white rounded-lg px-2 py-1.5 border border-slate-200">
                    Location %
                    <input
                      type="number" step="0.5" min="0" max="20"
                      className="text-[11px] border border-slate-200 rounded px-1 py-0.5 w-16 ml-auto"
                      value={sw?.locationPct ?? ''} disabled={!sw}
                      onChange={e => setMhfSwitch(t.id, { locationPct: e.target.value })}
                    />
                  </label>
                </div>

                {res?.message && (
                  <p className="text-xs text-slate-600 mb-1">{res.message}</p>
                )}
                {res?.shown?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="text-[11px] border border-slate-100 rounded-lg overflow-hidden w-full">
                      <thead className="bg-white text-slate-500">
                        <tr>
                          <th className="text-left px-2 py-1">Symbol</th>
                          <th className="text-right px-2 py-1">Price</th>
                          <th className="text-right px-2 py-1">RSI(14)</th>
                          <th className="text-right px-2 py-1">Vol ratio</th>
                          <th className="text-right px-2 py-1">Off 20d high</th>
                          <th className="text-left px-2 py-1">Passed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {res.shown.map((row, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1 font-medium">{row.symbol}</td>
                            <td className="px-2 py-1 text-right">{row.price}</td>
                            <td className="px-2 py-1 text-right">{row.rsi14}</td>
                            <td className="px-2 py-1 text-right">{row.volumeRatio}x</td>
                            <td className="px-2 py-1 text-right">{row.distFromHighPct}%</td>
                            <td className={`px-2 py-1 ${row.passed ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                              {row.passed ? 'YES' : row.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {wl?.watchlist?.length > 0 && (
                  <p className="text-xs text-slate-600 mt-1">
                    Tomorrow watchlist: {wl.watchlist.map(w => w.symbol).join(', ')}
                  </p>
                )}
                {wl?.message && !wl?.watchlist?.length && (
                  <p className="text-xs text-slate-500 mt-1">{wl.message}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </>
      )}

      {activeSection === 'AI' && (dailyRegime || dailyShown.length > 0) && (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
        {dailyRegime && (
          <p className="text-xs mb-3">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              dailyMode === 'BUY_DAY' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}>{dailyMode === 'BUY_DAY' ? 'BUY day' : 'SHORT day'}</span>
            <span className="text-slate-400 ml-2">
              SPY {dailyRegime.spyPrice} vs its 20d avg {dailyRegime.spy20dSma} ({dailyRegime.aboveSma20 ? 'above' : 'below'})
            </span>
          </p>
        )}
        {dailyShown.length > 0 && (
          <div className="overflow-x-auto">
            <table className="text-xs border border-slate-100 rounded-lg overflow-hidden w-full">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">Symbol</th>
                  <th className="text-left px-2 py-1">Dir</th>
                  <th className="text-right px-2 py-1">Price</th>
                  <th className="text-right px-2 py-1">20d/50d SMA</th>
                  <th className="text-right px-2 py-1">RSI(14)</th>
                  <th className="text-right px-2 py-1">Vol vs 20d avg</th>
                  <th className="text-right px-2 py-1">Daily Move</th>
                  <th className="text-center px-2 py-1">Result</th>
                </tr>
              </thead>
              <tbody>
                {dailyShown.map((c, i) => (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="px-2 py-1 font-semibold">{c.symbol}</td>
                    <td className="px-2 py-1">{c.direction}</td>
                    <td className="px-2 py-1 text-right tabular-nums">${c.price}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.sma20} / {c.sma50}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.rsi14}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.volumeRatio}x</td>
                    <td className="px-2 py-1 text-right tabular-nums">${c.dailyMove}</td>
                    <td className="px-2 py-1 text-center" title={c.reason || ''}>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        c.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>{c.passed ? 'Alerted' : 'Skipped'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          Up to 10 scanned candidates shown (pass or fail); hover "Skipped" for why. Alerted candidates appear as
          pending signals in the review list below this panel.
        </p>
      </div>
      )}

      {activeSection === 'SCALP' && (
      <>
      <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Intraday Scalp / Swing — Options (Call/Put only)</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">NOT backtest-validated</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-600" title="Same-day SCALP picks (live 5/16 = 31%)">
              <input
                type="checkbox"
                checked={!!scalpEnabled}
                disabled={scalpEnabled === null || scalpEnabledLoading}
                onChange={e => toggleScalpEnabled(e.target.checked)}
              />
              Auto-scan Scalp
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600" title="Multi-day SWING picks, switched separately">
              <input
                type="checkbox"
                checked={!!swingEnabled}
                disabled={swingEnabled === null || swingEnabledLoading}
                onChange={e => toggleSwingEnabled(e.target.checked)}
              />
              Auto-scan Swing
            </label>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Separate engine from AI Rapid/Daily signals — index ETFs (QQQ/SPY/DIA) plus a leveraged name (SOXL)
          for extra range, scanned alongside liquid
          stock movers. Rule engine: opening-range breakout/breakdown + VWAP hold/rejection + 9/21 EMA trend +
          volume confirmation (CALL needs all four; PUT needs the mirror image) — momentum alone is never enough.
          Only a FRESH break (first close beyond the opening range within the last 20 min) counts, and after a
          stop-out a symbol gets a 30-min cooldown (max 2 stop-outs per symbol per day).
          There isn&apos;t enough historical intraday data available to backtest this the way the other two engines
          are backtested, so this is a real-time rule scan, not a proven win rate. Targets are set at least 2x the
          planned risk. <strong>Scalp</strong> auto-closes on its own faster cycle and force-closes by end of day —
          never held overnight. <strong>Swing</strong> uses the same entry signal but a longer expiry (10+ days out)
          and a wider target/stop, and is left open across days like any other multi-day signal instead of being
          force-closed at end of day. Always generated unpublished — review each pick in the list below and
          publish individually before it's visible to users in the AI Rapid Profit section.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1">
            {['SCALP', 'SWING'].map(st => (
              <button
                key={st}
                type="button"
                onClick={() => setScalpStyle(st)}
                className={`text-xs px-3 py-1.5 rounded-lg transition ${
                  scalpStyle === st ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {st === 'SCALP' ? 'Scalp (same-day)' : 'Swing (multi-day)'}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={generateScalp}
          disabled={scalpLoading}
          className="text-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
        >
          {scalpLoading ? 'Scanning…' : `${scalpStyle === 'SCALP' ? 'Scalp' : 'Swing'}: scan for options picks`}
        </button>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Unpublished — created picks stay internal until you review and publish them individually from the list below.
        </p>
        {scalpMsg && <p className="text-xs text-slate-600 mt-2">{scalpMsg}</p>}
        {scalpScan.length > 0 && (
          <div className="w-full mt-3 overflow-x-auto">
            <table className="text-xs border border-slate-100 rounded-lg overflow-hidden w-full">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">Symbol</th>
                  <th className="text-right px-2 py-1">Price</th>
                  <th className="text-right px-2 py-1">VWAP</th>
                  <th className="text-right px-2 py-1">OR High/Low</th>
                  <th className="text-right px-2 py-1">9/21 EMA</th>
                  <th className="text-right px-2 py-1">R:R</th>
                  <th className="text-left px-2 py-1">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {scalpScan.map((r, i) => (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="px-2 py-1 font-semibold">{r.symbol}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.price != null ? `$${r.price}` : '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.vwap != null ? `$${r.vwap}` : '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {r.openingRangeHigh != null ? `$${r.openingRangeLow}–${r.openingRangeHigh}` : '—'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {r.ema9 != null ? `${r.ema9}/${r.ema21}` : '—'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.rewardRiskRatio != null ? `${r.rewardRiskRatio}:1` : '—'}</td>
                    <td className="px-2 py-1">
                      {r.result
                        ? <span className={`font-semibold ${r.strong ? 'text-amber-700' : 'text-emerald-700'}`}>{r.result}</span>
                        : <span className="text-slate-400 italic">{r.skipped || 'skipped'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Rapid Move — quick 1% moves on heavy volume</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">AUTO-ALERTS</span>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!rapidEnabled}
              disabled={rapidEnabled === null || rapidEnabledLoading}
              onChange={e => toggleRapidEnabled(e.target.checked)}
            />
            Scan + alert
          </label>
        </div>
        <p className="text-xs text-slate-500">
          Every minute 9:30am-4:00pm: any $1B+ stock that moves 1%+ up or down within 5 minutes on 2x+ its normal
          volume for that time of day. Every hit is listed on the Market page (Rapid Move tab). The strongest
          ones are sent straight to users as alerts (Notifications + Android push) — at most 10 a day, paced
          through the session (2 at the open, then about one more every 40 min), 15+ min apart, one per stock.
          Off = no scanning (saves ~130 marketdata.app credits a minute).
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-violet-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Intraday Option Formula — CALL/PUT Scalp + Swing + LEAP</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">AUTO-SCANNED</span>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!iofEnabled}
              disabled={iofEnabled === null || iofEnabledLoading}
              onChange={e => toggleIofEnabled(e.target.checked)}
            />
            Auto-scan new entries
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Same 5-check base (VWAP + 20 EMA + RSI 58-68 + recent-high break + volume &gt;=1.5x, mirrored for PUT,
          both requiring SPY+QQQ to agree) drives all three styles — SCALP (same-day, ATM, stop -30%/+40% sell
          half/+75% sell rest), SWING (10-21 days, slightly OTM, stop -35%/1.5R/2.5R/3.5R ladder), and LEAP
          (45-90 days, long-expiration-dated options, stop -40%/1.5R/2.5R/3.5R ladder) — decided per pick by
          whether the daily chart is ALSO strong (SWING) and, on top of that, whether the trend is sustained
          enough on a bigger SMA50/SMA100 read to also clear LEAP's bar. Auto-scans every 2 min 9:40-11:30am,
          every 5 min (strong setups only) 11:30-1:30pm, every 2 min 1:30-3:00pm, every 1 min 3:00-3:55pm (no
          same-day expiry after 3pm) — LEAP's own daily-bar gate is only re-checked once an hour (on the hour),
          its own much slower "respective scan interval" appropriate for a 45-90 day hold. Stop-loss checked
          every 30s, profit every 1 min, on already-open positions regardless of the toggle above. On a choppy
          day (SPY chopping across its own VWAP) entries tighten to 6/6 checks, SPY/QQQ only, and (for SCALP) a
          single smaller target. Always generated unpublished.
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <button
            type="button"
            onClick={runIofPrepare}
            disabled={iofLoading != null}
            className="text-xs bg-slate-700 hover:bg-slate-800 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
          >
            {iofLoading === 'PREPARE' ? 'Building…' : "Rebuild today's hot list"}
          </button>
          <button
            type="button"
            onClick={runIofScan}
            disabled={iofLoading != null}
            className="text-xs bg-violet-600 hover:bg-violet-700 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
          >
            {iofLoading === 'SCAN' ? 'Scanning…' : 'Scan now for 1 alert'}
          </button>
        </div>
        {iofPrepareResult?.message && <p className="text-xs text-slate-600 mb-1">{iofPrepareResult.message}</p>}
        {iofScanResult?.message && (
          <p className="text-xs text-slate-600">
            {iofScanResult.choppy && <span className="text-amber-600 font-semibold">[CHOPPY DAY] </span>}
            {iofScanResult.message}
          </p>
        )}
        {iofScanResult?.created?.length > 0 && (
          <p className="text-xs text-emerald-700 mt-1">
            Alerted: {iofScanResult.created.map(c => `${c.symbol} (${
              c.companyName?.includes('LEAP') ? 'LEAP' : c.companyName?.includes('SWING') ? 'SWING' : 'SCALP'
            })`).join(', ')}
          </p>
        )}
      </div>

      </>
      )}

      {activeSection === 'ADXIDM' && (
      <>
      <div className="bg-white rounded-2xl border border-sky-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Auto-Tracked Symbol — Multi-Timeframe (1m/5m/15m) + ADX/DMI</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">MTF</span>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!mtfEnabled}
              disabled={mtfEnabled === null || mtfEnabledLoading}
              onChange={e => toggleMtfEnabled(e.target.checked)}
            />
            Auto-scan
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          When Auto-scan is on, runs every 5 minutes during market hours for ONE tracked symbol (defaults to QQQ).
          <strong> Off by default</strong> — on 10/02 it made 12 of its 14 trades losers (cheap same-day options in a
          flat market), so it stays off unless you turn it on. To switch symbols, set a new one below; it replaces
          whatever was tracked before.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm">
            <span className="text-slate-400">Currently tracking:</span>{' '}
            <span className="font-mono font-bold text-slate-900">{mtfTrackedSymbol || '— none (cancelled) —'}</span>
          </div>
          <input
            type="text"
            placeholder="New symbol (e.g. QQQ)"
            value={mtfNewSymbol}
            onChange={e => setMtfNewSymbol(e.target.value.toUpperCase())}
            className="border rounded-lg px-3 py-2 text-sm font-mono w-40"
          />
          <button
            type="button"
            onClick={() => setTrackedSymbol(mtfNewSymbol.trim())}
            disabled={mtfTrackedLoading || !mtfNewSymbol.trim()}
            className="text-sm bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
          >
            {mtfTrackedLoading ? 'Updating…' : 'Track this symbol'}
          </button>
          <button
            type="button"
            onClick={() => setTrackedSymbol(null)}
            disabled={mtfTrackedLoading || !mtfTrackedSymbol}
            className="text-sm bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
          >
            Cancel tracking
          </button>
        </div>
        {mtfTrackedMsg && <p className="text-xs text-slate-600 mt-2">{mtfTrackedMsg}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-sm font-semibold text-slate-800">Manual One-Off Scan</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Test any symbol on demand — same engine, but doesn't change what's auto-tracked above. Always unpublished;
          review and publish from the list below.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <input
            type="text"
            placeholder="Symbol (e.g. AAPL)"
            value={mtfSymbol}
            onChange={e => setMtfSymbol(e.target.value.toUpperCase())}
            className="border rounded-lg px-3 py-2 text-sm font-mono w-40"
          />
          <button
            type="button"
            onClick={generateMtf}
            disabled={mtfLoading || !mtfSymbol.trim()}
            className="text-sm bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
          >
            {mtfLoading ? 'Scanning…' : 'Scan symbol'}
          </button>
        </div>
        {mtfMsg && <p className="text-xs text-slate-600 mb-2">{mtfMsg}</p>}
        {mtfRead && (
          <>
            <div className="text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 rounded-lg p-3 mb-3">
              <div><span className="text-slate-400">Bull conf</span><br /><span className="font-bold text-emerald-700">{mtfRead.bullConfidence}%</span></div>
              <div><span className="text-slate-400">Bear conf</span><br /><span className="font-bold text-rose-700">{mtfRead.bearConfidence}%</span></div>
              <div><span className="text-slate-400">1m/5m/15m votes</span><br /><span className="font-bold">{(mtfRead.mtfVotes1m5m15m || []).join(' / ')}</span></div>
              <div><span className="text-slate-400">Alerts today</span><br /><span className="font-bold">{mtfRead.alertsToday}</span></div>
              <div><span className="text-slate-400">Still needed</span><br /><span className="font-bold">{mtfRead.stillNeededForDailyTarget}</span></div>
              <div className="col-span-2 sm:col-span-1"><span className="text-slate-400">Behind pace (Grade C)</span><br /><span className="font-bold">{mtfRead.behindPace ? 'Yes' : 'No'}</span></div>
              <div className="col-span-2 sm:col-span-1"><span className="text-slate-400">Min ADX to count as trending</span><br /><span className="font-bold">{mtfRead.minAdxThreshold}</span></div>
            </div>
            {/* Fine-tuned: full per-timeframe ADX/DMI breakdown (was just a single "ADX (1m)"
                number) — shows exactly what fed each timeframe's vote and the weighted
                confidence score above: EMA9/21 trend, ADX strength, +DI/-DI bias, whether
                that timeframe cleared the trending threshold, and its own volume read. */}
            {Array.isArray(mtfRead.adxBreakdown) && mtfRead.adxBreakdown.length > 0 && (
              <div className="overflow-x-auto">
                <table className="text-xs border border-slate-100 rounded-lg overflow-hidden w-full">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1">Timeframe</th>
                      <th className="text-left px-2 py-1">EMA trend</th>
                      <th className="text-right px-2 py-1">ADX</th>
                      <th className="text-right px-2 py-1">+DI</th>
                      <th className="text-right px-2 py-1">-DI</th>
                      <th className="text-left px-2 py-1">DI bias</th>
                      <th className="text-left px-2 py-1">Trending?</th>
                      <th className="text-left px-2 py-1">Volume rising</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mtfRead.adxBreakdown.map((tf, i) => (
                      <tr key={i} className="border-t border-slate-50">
                        <td className="px-2 py-1 font-semibold">{tf.timeframe}</td>
                        <td className={`px-2 py-1 font-semibold ${tf.trend === 'UP' ? 'text-emerald-700' : tf.trend === 'DOWN' ? 'text-rose-700' : 'text-slate-400'}`}>
                          {tf.trend}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{tf.adx}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{tf.plusDi}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{tf.minusDi}</td>
                        <td className={`px-2 py-1 font-semibold ${tf.diBias === 'BULL' ? 'text-emerald-700' : tf.diBias === 'BEAR' ? 'text-rose-700' : 'text-slate-400'}`}>
                          {tf.diBias}
                        </td>
                        <td className="px-2 py-1">
                          {tf.trending
                            ? <span className="text-emerald-700 font-semibold">Trending</span>
                            : <span className="text-amber-600 italic">Choppy (below {mtfRead.minAdxThreshold})</span>}
                        </td>
                        <td className="px-2 py-1">{tf.volumeIncreasing ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {/* Two groups: the signals themselves, and the live watch feeds (no signals). */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
      {[
        { title: 'Signals', tabs: [
          { id: 'PENDING', label: `Pending review${pendingCount ? ` (${pendingCount})` : ''}` },
          { id: 'ALL', label: 'All' },
          { id: 'OPEN', label: 'Open' },
          { id: 'STOCK', label: 'Stocks' },
          { id: 'OPTION', label: 'Options' },
          { id: 'SCALP', label: 'Scalp/Swing' },
          { id: 'CLOSED', label: 'Closed' },
        ] },
        { title: 'Live watch', tabs: [
          { id: 'RAPID_MOVES', label: '⚡ Rapid Moves' },
          { id: 'RAPID_OPT', label: '⚡ Rapid Options' },
        ] },
      ].map(group => (
      <div key={group.title}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1 px-1">{group.title}</div>
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {group.tabs.map(f => (
          <button
            key={f.id}
            type="button"
            onClick={() => {
              setFilter(f.id);
              if (f.id === 'SCALP') markScalpSeen();
            }}
            className={`relative text-xs px-3 py-1.5 rounded-lg transition ${
              filter === f.id ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600'
            }`}
          >
            {f.label}
            {f.id === 'SCALP' && newScalpCount > 0 && (
              <span
                className="absolute -top-1 -right-1 flex h-3.5 w-3.5"
                title={`${newScalpCount} new intraday pick${newScalpCount === 1 ? '' : 's'}`}
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-rose-600 items-center justify-center text-[8px] font-bold text-white">
                  {newScalpCount > 9 ? '9+' : newScalpCount}
                </span>
              </span>
            )}
          </button>
        ))}
      </div>
      </div>
      ))}
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6 grid md:grid-cols-2 gap-3">
          <input placeholder="Symbol *" value={form.symbol}
            onChange={e => setForm({...form, symbol: e.target.value.toUpperCase()})}
            onBlur={e => lookupSymbol(e.target.value)}
            required className="border rounded-lg px-3 py-2 font-mono" />
          <input placeholder="Company Name" value={form.companyName} onChange={e => setForm({...form, companyName: e.target.value})} className="border rounded-lg px-3 py-2" />
          <select value={form.signalType} onChange={e => {
            const nextType = e.target.value;
            const wasOption = form.signalType?.startsWith('OPTION');
            const isNowOption = nextType.startsWith('OPTION');
            if (wasOption !== isNowOption) {
              // Crossing the stock/option boundary: clear the price fields that
              // don't apply to the new type. Bug fix — a stock-scale entryPrice
              // typed before switching to OPTION used to survive the switch and
              // silently become the option's "entry" if buyTarget was left blank
              // (Signals.jsx falls back to entryPrice), producing a wildly wrong
              // gain% once diffed against a genuine small option premium.
              setForm({ ...form, signalType: nextType, entryPrice: '', targetPrice: '', buyTarget: '', sellTarget1: '' });
            } else {
              setForm({ ...form, signalType: nextType });
            }
          }} className="border rounded-lg px-3 py-2">
            <option value="STOCK_BUY">Stock Buy</option>
            <option value="STOCK_SELL">Stock Sell</option>
            <option value="OPTION_CALL">Option Call</option>
            <option value="OPTION_PUT">Option Put</option>
          </select>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="border rounded-lg px-3 py-2">
            {form.status === 'CANDIDATE' && <option value="CANDIDATE">Candidate (draft — becomes OPEN on publish)</option>}
            <option value="OPEN">OPEN</option>
            <option value="BOOKED_PROFIT">Booked Profit</option>
            <option value="STOPPED_OUT">Stopped Out</option>
            <option value="CLOSED">CLOSED</option>
          </select>
          <input type="number" step="0.01" placeholder="Gain/Loss % override (optional)" value={form.gainOverridePct}
            onChange={e => setForm({...form, gainOverridePct: e.target.value})}
            className="border rounded-lg px-3 py-2" title="Leave empty for auto-calculated %" />


          {!isOption && (
            <>
              <input type="number" step="0.01" placeholder="Entry / Recommended" value={form.entryPrice} onChange={e => setForm({...form, entryPrice: e.target.value})} className="border rounded-lg px-3 py-2" />
              <input type="number" step="0.01" placeholder="Target Price" value={form.targetPrice} onChange={e => setForm({...form, targetPrice: e.target.value})} className="border rounded-lg px-3 py-2" />
            </>
          )}
          {isOption && (
            <>
              <div className="md:col-span-2 -mb-1">
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  Buy/Sell below are the <strong>option premium</strong> (e.g. $2.50 per contract) — NOT the stock price
                  or the strike price. Entering a stock/strike-scale number here is the #1 cause of a wildly wrong
                  gain/loss % on the user-facing Signals page.
                </p>
              </div>
              <input type="number" step="0.01" placeholder="Buy Target — option premium ($)" value={form.buyTarget}
                onChange={e => setForm({...form, buyTarget: e.target.value})}
                title="The OPTION PREMIUM at entry (e.g. 2.50), not the stock price or strike price"
                className={`border rounded-lg px-3 py-2 ${
                  form.buyTarget && form.strikePrice && Number(form.buyTarget) > Number(form.strikePrice)
                    ? 'border-rose-400 bg-rose-50' : ''
                }`} />
              <input type="number" step="0.01" placeholder="Sell Target 1 — option premium ($)" value={form.sellTarget1}
                onChange={e => setForm({...form, sellTarget1: e.target.value})}
                title="The OPTION PREMIUM target, not the stock price or strike price"
                className="border rounded-lg px-3 py-2" />
              <input type="number" step="0.01" placeholder="Strike Price" value={form.strikePrice} onChange={e => setForm({...form, strikePrice: e.target.value})} className="border rounded-lg px-3 py-2" />
              <input type="date" value={form.expiryDate} onChange={e => setForm({...form, expiryDate: e.target.value})} className="border rounded-lg px-3 py-2" title="Option Expiry" />
              {form.buyTarget && form.strikePrice && Number(form.buyTarget) > Number(form.strikePrice) && (
                <p className="md:col-span-2 -mt-2 text-[11px] text-rose-600 font-semibold">
                  ⚠ Buy Target (${form.buyTarget}) is higher than the strike price (${form.strikePrice}) — this almost
                  always means a stock or strike price was typed into the premium field by mistake. Double-check before
                  saving.
                </p>
              )}
            </>
          )}
          <input type="number" step="0.01" placeholder="Stop Loss" value={form.stopLoss} onChange={e => setForm({...form, stopLoss: e.target.value})} className="border rounded-lg px-3 py-2" />
          <div>
            <label className="text-[10px] text-slate-400 uppercase">Signal Start</label>
            <input type="datetime-local" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})} className="border rounded-lg px-3 py-2 w-full" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase">Signal End (empty = open)</label>
            <input type="datetime-local" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})} className="border rounded-lg px-3 py-2 w-full" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} /> Published</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.notificationEnabled} onChange={e => setForm({...form, notificationEnabled: e.target.checked})} /> Notifications</label>
          <button type="submit" className="md:col-span-2 bg-slate-900 text-white py-2.5 rounded-lg font-medium">
            {editingId ? 'Update Signal' : 'Create Signal'}
          </button>
        </form>
      )}

      {filter === 'RAPID_OPT' ? <RapidOptionsPanel /> : filter === 'RAPID_MOVES' ? <RapidMovesPanel /> : (
      <div className="space-y-3">
        {filteredSignals.map(s => {
          const q = quotes[s.symbol];
          const isOpt = s.signalType?.startsWith('OPTION');
          const rec = isOpt ? (s.buyTarget ?? s.entryPrice) : s.entryPrice;
          const tgt = isOpt ? s.sellTarget1 : s.targetPrice;
          const mkt = q?.price != null ? Number(q.price) : null;
          const closedStatus = ['BOOKED_PROFIT', 'STOPPED_OUT', 'CLOSED'].includes((s.status || '').toUpperCase());
          // Closed signals: show the REAL realized gain captured at close time (exitPrice-based),
          // not a live quote that has since moved away from the actual exit. Open signals still
          // show live mark-to-market.
          const gain = s.gainOverridePct != null
            ? Number(s.gainOverridePct)
            : closedStatus
              ? (s.gainPct != null ? Number(s.gainPct) : null)
              : (isOpt ? null : calcGain(rec, mkt, s.signalType, false));
          const st = s.status || 'OPEN';

          const isBuy = s.signalType?.includes('BUY') || s.signalType?.includes('CALL');
          // Weekly (AI Rapid Profit) ids can collide with plain StockSignal ids since
          // they're separate tables — namespace the key/preview-toggle id so two rows
          // never share one and toggling one row's preview doesn't also open the other.
          const rowKey = s.source === 'WEEKLY' ? `weekly-${s.id}` : s.id;
          return (
            <div key={rowKey} className={`relative overflow-hidden rounded-2xl border p-4 flex flex-wrap justify-between items-center gap-3 shadow-md bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white ${
              isBuy ? 'border-emerald-500/30' : 'border-rose-500/30'
            }`}>
              <div className={`absolute inset-y-0 left-0 w-1.5 ${isBuy ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              <div className="min-w-0 pl-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/stock/${encodeURIComponent(s.symbol)}`} className="text-xl font-extrabold tracking-tight text-white hover:text-emerald-300">
                    {s.symbol}
                  </Link>
                  {s.companyName && (
                    <span className="text-xs text-slate-400 truncate max-w-[180px]">
                      {(s.companyName || '')
                        .replace(/^\[AI\](\[\w+\])?\s*/i, '')
                        .replace(/^\[SCALP\]\s*/i, '')
                        .replace(/^\[SWING\]\s*/i, '')
                        .replace(/^\[LEAP\]\s*/i, '')
                        .replace(/^\[MTF\]\s*/i, '')
                        .replace(/^\[DAILY\]\s*/i, '')
                        .replace(/^\[MHF\](\[[\w-]+\])?\s*/i, '')
                        .replace(/^\[IOF\](\[\w+\])?\s*/i, '')
                        .replace(/^\[POC\]\s*/i, '')
                        .trim()}
                    </span>
                  )}
                  {s.source === 'WEEKLY' && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-400/15 text-indigo-300" title="AI Rapid Profit — weekly backtested pick">AI RAPID</span>
                  )}
                  {(s.companyName || '').startsWith('[SCALP]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">SCALP</span>
                  )}
                  {(s.companyName || '').startsWith('[SWING]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">SWING</span>
                  )}
                  {(s.companyName || '').startsWith('[MTF]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-400/15 text-sky-300" title="Multi-timeframe + ADX/DMI engine, tracked symbol">MTF</span>
                  )}
                  {(s.companyName || '').startsWith('[DAILY]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300" title="Today's Buy/Short Formula — auto-scanned engine">DAILY</span>
                  )}
                  {(() => {
                    const m = /^\[MHF\]\[([\w-]+)\]/i.exec(s.companyName || '');
                    if (!m) return null;
                    return (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-400/15 text-violet-300" title="Multi-Horizon Formula engine">
                        MHF {m[1]}
                      </span>
                    );
                  })()}
                  {isPocTagged(s) && (
                    <>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-400/15 text-orange-300" title="Option candidate — from a real-time stock move (admin only until published)">POC</span>
                      <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${
                        s.signalType === 'OPTION_PUT' ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'
                      }`}>
                        {s.signalType === 'OPTION_PUT' ? '▼ DOWN · PUT' : '▲ UP · CALL'}
                      </span>
                    </>
                  )}
                  {(s.companyName || '').startsWith('[IOF][SCALP]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-300" title="Intraday Option Formula — same-day scalp">IOF SCALP</span>
                  )}
                  {(s.companyName || '').startsWith('[IOF][SWING]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-300" title="Intraday Option Formula — 1-5 day swing">IOF SWING</span>
                  )}
                  {(s.companyName || '').startsWith('[IOF][LEAP]') && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-400/15 text-purple-300" title="Intraday Option Formula — 45-90 day long-dated option, checked hourly">IOF LEAP</span>
                  )}
                  {(() => {
                    const m = /^\[AI\]\[(\w+)\]/i.exec(s.companyName || '');
                    if (!m) return null;
                    const labels = { '3D': '3-4 DAY', '1W': '1 WEEK', '2W': '2 WEEK', '1M': '1 MONTH' };
                    return (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-400/15 text-indigo-300">
                        {labels[m[1].toUpperCase()] || m[1].toUpperCase()}
                      </span>
                    );
                  })()}
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    isBuy ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/15 text-rose-300'
                  }`}>{s.signalType?.replace(/_/g,' ')}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    st === 'BOOKED_PROFIT' ? 'bg-emerald-400/15 text-emerald-300' :
                    st === 'STOPPED_OUT' ? 'bg-rose-400/15 text-rose-300' :
                    st === 'OPEN' ? 'bg-sky-400/15 text-sky-300' : 'bg-white/10 text-slate-300'
                  }`}>{st.replace(/_/g,' ')}</span>
                  {s.published && <span className="text-[10px] text-emerald-400 font-medium">Published</span>}
                </div>
                {isPocTagged(s) && s.candidateNote && (
                  <p className="text-xs text-orange-200/90 mt-1">{s.candidateNote}</p>
                )}
                <p className="text-sm text-slate-300 mt-1">
                  {isPocTagged(s) && s.buyTarget == null
                    ? <>
                        Strike ${s.strikePrice ?? '—'} · Exp {s.expiryDate || '—'}
                        {!s.published && <span className="ml-2 text-amber-300">· Enter premium (Edit) to publish</span>}
                      </>
                    : isOpt
                      ? `Buy $${s.buyTarget ?? s.entryPrice} · Sell $${s.sellTarget1 || '—'}`
                      : `Entry $${s.entryPrice} → Target $${s.targetPrice}`}
                  {s.stopLoss && ` · SL $${s.stopLoss}`}
                </p>
                {/* When it was alerted / published / closed — on every signal, every engine */}
                <p className="text-[11px] text-slate-400 mt-1">
                  Alerted {fmtET(s.createdAt || s.startDate) || '—'}
                  {s.publishedAt && <> · Published {fmtET(s.publishedAt)}</>}
                  {s.endDate && <> · Closed {fmtET(s.endDate)}</>}
                </p>
                {q && (
                  <p className="text-xs mt-1.5 text-slate-400">
                    <span className="font-bold text-white text-sm">${Number(q.price).toFixed(2)}</span>
                    <span className="mx-1">·</span>
                    H {Number(q.dayHigh).toFixed(2)} L {Number(q.dayLow).toFixed(2)}
                    {q.volume != null && q.volume !== '' && (
                      <span className="ml-1">· Vol {q.volume}</span>
                    )}
                    {!closedStatus && gain != null && (
                      <span className={`ml-2 font-bold ${gain >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {gain >= 0 ? '+' : ''}{gain.toFixed(2)}%
                      </span>
                    )}
                  </p>
                )}
                {closedStatus && (
                  <p className="text-xs mt-1.5 text-slate-400">
                    {s.exitPrice != null && <span>Exited @ ${Number(s.exitPrice).toFixed(2)}</span>}
                    {gain != null && (
                      <span className={`ml-2 font-bold ${gain >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {gain >= 0 ? '+' : ''}{gain.toFixed(2)}% realized
                      </span>
                    )}
                  </p>
                )}
                {previewId === rowKey && (
                  <div className="mt-2 bg-white/[0.03] border border-white/10 rounded-lg p-3 text-xs text-slate-300 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><span className="text-slate-500">Entry</span><br /><span className="font-semibold text-white">${s.entryPrice ?? s.buyTarget ?? '—'}</span></div>
                    <div><span className="text-slate-500">Target 1</span><br /><span className="font-semibold text-white">${s.targetPrice ?? s.sellTarget1 ?? '—'}</span></div>
                    {s.sellTarget2 != null && <div><span className="text-slate-500">Target 2</span><br /><span className="font-semibold text-white">${s.sellTarget2}</span></div>}
                    {s.sellTarget3 != null && <div><span className="text-slate-500">Target 3</span><br /><span className="font-semibold text-white">${s.sellTarget3}</span></div>}
                    <div><span className="text-slate-500">Stop loss</span><br /><span className="font-semibold text-white">${s.stopLoss ?? '—'}</span></div>
                    {s.strikePrice != null && <div><span className="text-slate-500">Strike</span><br /><span className="font-semibold text-white">${s.strikePrice}</span></div>}
                    {s.expiryDate && <div><span className="text-slate-500">Expiry</span><br /><span className="font-semibold text-white">{s.expiryDate}</span></div>}
                    <div><span className="text-slate-500">Created</span><br /><span className="font-semibold text-white">{s.createdAt ? fmtET(s.createdAt) : '—'}</span></div>
                    <div className="col-span-2 sm:col-span-3"><span className="text-slate-500">Raw tag</span><br /><span className="font-mono">{s.companyName || '—'}</span></div>
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => setPreviewId(previewId === rowKey ? null : rowKey)}
                  className="text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 px-3 py-1.5 rounded-lg"
                  title="Quick preview — full details without opening Edit"
                >
                  👁 {previewId === rowKey ? 'Hide' : 'Preview'}
                </button>
                {s.source !== 'WEEKLY' && (
                  <button onClick={() => edit(s)} className="text-xs bg-white/10 border border-white/10 text-white hover:bg-white/20 px-3 py-1.5 rounded-lg" title="Review and change values before publishing">Edit</button>
                )}
                {!closedStatus && !s.published && (
                  <>
                    <button onClick={() => publishPending(s)} className="text-xs bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-1.5 rounded-lg" title="Publish as-is, no changes needed">
                      Publish
                    </button>
                    <button onClick={() => discardPending(s)} className="text-xs bg-white/5 border border-rose-400/30 text-rose-300 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg">
                      Discard
                    </button>
                  </>
                )}
                {(st === 'OPEN' || !s.endDate) && s.published && (
                  <button onClick={() => closeSignal(s)} className="text-xs bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-1.5 rounded-lg" title="Close signal (e.g. target met)">
                    Close
                  </button>
                )}
                {s.source !== 'WEEKLY' && !(!closedStatus && !s.published) && (
                  <button onClick={() => remove(s)} className="text-xs bg-white/5 border border-rose-400/30 text-rose-300 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg">Delete</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
